import React from 'react';
import { IconCheck } from '@tabler/icons-react';
import './FormField.css';

interface FormFieldProps {
  label: string;
  /** Dims + disables the field (e.g. time fields when All day is checked). */
  disabled?: boolean;
  /** When set, flags the field with a red (soft) border + this message below. */
  error?: string;
  /**
   * Marks the field as mandatory with a badge on the notch label. Pair it with
   * `outstanding` so the badge says which of the two things it means: still
   * wanted (amber badge + warm ring on the control) or satisfied (a quiet
   * green tick). Amber rather than the red `error` treatment deliberately —
   * an empty required field the operator has not reached yet is outstanding
   * work, not a mistake, and it reads as the same "still to do" state the
   * check-in panels use.
   */
  required?: boolean;
  /** Only meaningful with `required`: the field is required and still empty. */
  outstanding?: boolean;
  children: React.ReactNode;
}

/** Notched floating-label wrapper for a native input/select/textarea. */
export const FormField: React.FC<FormFieldProps> = ({
  label,
  disabled,
  error,
  required,
  outstanding,
  children,
}) => (
  <div
    className={`side-form-field ${disabled ? 'side-form-field--disabled' : ''} ${
      error ? 'side-form-field--error' : ''
    } ${required && outstanding ? 'side-form-field--outstanding' : ''}`}
  >
    <span className="side-form-field__label">
      {label}
      {required && outstanding && (
        <span className="side-form-field__req">Required</span>
      )}
      {required && !outstanding && (
        <span className="side-form-field__req side-form-field__req--met" aria-label="Required — provided">
          <IconCheck size={11} stroke={2.5} />
        </span>
      )}
    </span>
    {children}
    {error && <span className="side-form-field__error">{error}</span>}
  </div>
);
